import { useState, type CSSProperties, type ReactNode } from 'react';
import type { FounderEstado, FounderTier } from '../../../api';
import { numero, t } from '../../../i18n/idioma';
import { Coroa, Moedas, Selo } from './desenhos';
import { imagem, itensDo, mantoDo, nivel, nomeDo, packDe, reais, TIERS, TRIO, tierDe, type Icone } from './dados';
import './founders.css';

const PASSOS = [1, 2, 3, 4] as const;
const GARANTIAS = [1, 2] as const;

function IconeDoItem({ icone, id }: { icone: Icone; id: FounderTier }) {
  switch (icone) {
    case 'vip': return <Coroa />;
    case 'selo': return <Selo metal={tierDe(id).metal} tam={18} />;
    case 'corcel': return <img src={imagem(`cav-${id}`)} alt="" />;
    case 'manto': return <img src={imagem(`manto-${id}`)} alt="" />;
    case 'cajado': return <img src={imagem('cajado')} alt="" />;
    case 'ouro': return <Moedas />;
    default: return <span className="lp-f-marca">✓</span>;
  }
}

/** A faixa do herói: os founders aparecem logo na primeira dobra, abaixo do pré-registro em peso. */
export function FounderFaixa({ celular }: { celular?: boolean }) {
  return (
    <a className={celular ? 'lp-f-faixa lp-f-faixa--cel' : 'lp-f-faixa lp-f-faixa--pc'} href="#fundadores">
      <img src={imagem('monta-lorde')} alt="" />
      <span className="lp-f-faixa__t">
        <b><Selo metal="#e6c452" />{t('landing.fund.faixa.titulo')}</b>
        {t('landing.fund.faixa.texto')}
      </span>
      <span className="lp-f-faixa__ir">{t('landing.fund.faixa.ir')}</span>
    </a>
  );
}

function Cartao({ estado, id, onComprar }: { estado: FounderEstado; id: FounderTier; onComprar(id: FounderTier): void }) {
  const tv = tierDe(id);
  const pack = packDe(estado, id);
  const fora = !pack?.ativo;
  const semPagamento = !estado.pagamentoDisponivel;
  const preco = pack?.centavos ?? 0;
  return (
    <article className={fora ? 'lp-f-pack is-off' : 'lp-f-pack'} style={{ '--metal': tv.metal, '--robe': tv.robe } as CSSProperties}>
      <div className="lp-f-pack__cab">
        <small>{t('landing.fund.fundador')}</small>
        <h3><Selo metal={tv.metal} tam={16} />{nomeDo(id)}</h3>
        <span className="lp-f-preco">{reais(preco)}</span>
        <img src={imagem(`monta-${id}`)} alt={t('landing.fund.montaAlt', { nome: nomeDo(id) })} />
      </div>
      <ul className="lp-f-lista">
        {itensDo(id, pack).map((it, i) => (
          <li key={i}>
            <span><IconeDoItem icone={it.icone} id={id} /></span>
            <span>{it.texto}{it.exclusivo ? <em className="lp-f-exclusivo">{t('landing.fund.exclusivo')}</em> : null}{it.nota ? <small>{it.nota}</small> : null}</span>
          </li>
        ))}
      </ul>
      <div className="lp-f-pack__pe">
        {fora
          ? <><button type="button" className="lp-btn" disabled>{t('landing.fund.indisponivel')}</button><p>{t('landing.fund.indisponivelNota')}</p></>
          : <>
            <button type="button" className="lp-btn lp-btn--fund" disabled={semPagamento} onClick={() => onComprar(id)}>
              {t('landing.fund.apoiar', { v: reais(preco) })}
            </button>
            <p>{semPagamento ? t('landing.fund.semPagamento') : t('landing.fund.apoiarNota')}</p>
          </>}
      </div>
    </article>
  );
}

/** A prévia da carta do correio do jogo (o correio ainda será criado; a carta chega no primeiro login). */
function Carta({ estado }: { estado: FounderEstado }) {
  const [aba, setAba] = useState<FounderTier>('cavaleiro');
  const n = nivel(aba);
  const pack = packDe(estado, aba);
  // Cada pack traz só o próprio corcel e o próprio manto (Renato, 25/10/2026): nada dos packs de baixo.
  const anexos: { icone: ReactNode; nome: string }[] = [
    { icone: <Coroa largura={30} />, nome: t('landing.fund.anexo.vip', { n: numero(pack?.vipDias ?? 0) }) },
    { icone: <img src={imagem(`cav-${aba}`)} alt="" />, nome: t('landing.fund.corcel', { nome: nomeDo(aba) }) },
    { icone: <img src={imagem(`manto-${aba}`)} alt="" />, nome: t('landing.fund.anexo.manto', { cor: mantoDo(aba) }) },
    ...(n >= 3 ? [{ icone: <img src={imagem('cajado')} alt="" />, nome: t('landing.fund.item.cajado') }] : []),
    { icone: <Moedas tam={32} />, nome: t('landing.fund.item.ouro', { n: numero(pack?.ouro ?? 0) }) }
  ];
  return (
    <>
      <div className="lp-f-abas" role="tablist" aria-label={t('landing.fund.cartaTitulo')}>
        {TIERS.map(x => (
          <button key={x.id} type="button" role="tab" aria-selected={x.id === aba} onClick={() => setAba(x.id)}>
            <Selo metal={x.metal} />{nomeDo(x.id)}
          </button>
        ))}
      </div>
      <div className="lp-f-carta" role="tabpanel">
        <div className="lp-f-carta__cab">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
          {t('landing.fund.carta.correio')}<span>{t('landing.fund.carta.de')}</span>
        </div>
        <div className="lp-f-carta__papel">
          <p><b>{t('landing.fund.carta.ola', { nome: nomeDo(aba) })}</b></p>
          <p>{t('landing.fund.carta.p1')}</p>
          <p>{t('landing.fund.carta.p2')}</p>
          <p className="lp-f-assina">{t('landing.fund.carta.assina1')}<br />{t('landing.fund.carta.assina2')}</p>
        </div>
        <div className="lp-f-anexos">
          {anexos.map((a, i) => <div className="lp-f-anexo" key={i}>{a.icone}<span>{a.nome}</span></div>)}
        </div>
        <div className="lp-f-carta__pe">
          <span>{t('landing.fund.carta.pe')}</span>
          <span className="lp-btn lp-btn--fund" aria-hidden="true">{t('landing.fund.carta.receber')}</span>
        </div>
      </div>
    </>
  );
}

function Tabela({ estado }: { estado: FounderEstado }) {
  const sim = <span className="lp-f-s">✓</span>;
  const nao = <span className="lp-f-n">—</span>;
  const linhas: [string, (id: FounderTier, n: number) => ReactNode][] = [
    [t('landing.fund.tab.preco'), id => reais(packDe(estado, id)?.centavos ?? 0)],
    [t('landing.fund.tab.vip'), id => t('landing.fund.tab.dias', { n: numero(packDe(estado, id)?.vipDias ?? 0) })],
    [t('landing.fund.tab.selo'), id => t(`landing.fund.metal.${id}`)],
    [t('landing.fund.tab.corceis'), id => nomeDo(id)],
    [t('landing.fund.tab.mantos'), id => mantoDo(id)],
    [t('landing.fund.tab.ouro'), id => numero(packDe(estado, id)?.ouro ?? 0)],
    [t('landing.fund.tab.mural'), () => sim],
    [t('landing.fund.tab.vaga'), (_, n) => (n >= 2 ? sim : nao)],
    [t('landing.fund.tab.discord'), () => sim],
    [t('landing.fund.tab.cajado'), (_, n) => (n >= 3 ? sim : nao)],
    [t('landing.fund.tab.reserva'), (_, n) => (n >= 3 ? sim : nao)]
  ];
  return (
    <details className="lp-f-comparar">
      <summary>{t('landing.fund.comparar')}</summary>
      <div className="lp-f-tab-wrap">
        <table className="lp-f-tabela">
          <thead>
            <tr><th />{TIERS.map(x => <th key={x.id} scope="col"><Selo metal={x.metal} /> {nomeDo(x.id)}</th>)}</tr>
          </thead>
          <tbody>
            {linhas.map(([rotulo, f]) => (
              <tr key={rotulo}>
                <th scope="row">{rotulo}</th>
                {TIERS.map((x, i) => <td key={x.id}>{f(x.id, i + 1)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** A seção dos Founder Packs na página inicial: a compra abre aqui mesmo, num modal (sem página própria). */
export function FounderSecao({ estado, onComprar, onSubir, irAoFormulario }: {
  estado: FounderEstado;
  onComprar(id: FounderTier): void;
  onSubir(): void;
  irAoFormulario(): void;
}) {
  const [antesNota, depoisNota] = t('landing.fund.nota', { link: '\u0000' }).split('\u0000');
  return (
    <section className="lp-secao lp-f" id="fundadores" aria-labelledby="lp-t-fund">
      <div className="lp-envolve">
        <div className="lp-secao__cab lp-secao__cab--centro">
          <span className="lp-sobre">{t('landing.fund.sobre')}</span>
          <h2 id="lp-t-fund">{t('landing.fund.titulo')}</h2>
          <p>{t('landing.fund.texto')}</p>
        </div>

        <figure className="lp-f-trio">
          <picture>
            <source type="image/webp" srcSet={TRIO.webp} />
            <img src={TRIO.jpg} width={1200} height={560} alt={t('landing.fund.trioAlt')} loading="lazy" decoding="async" />
          </picture>
        </figure>

        <div className="lp-f-sempre" role="note">
          <Selo metal="#e6c452" tam={26} />
          <div>
            <b>{t('landing.fund.sempre.titulo')}</b>
            <p>{t('landing.fund.sempre.texto')}</p>
          </div>
        </div>

        <div className="lp-f-grade">
          {TIERS.map(x => <Cartao key={x.id} estado={estado} id={x.id} onComprar={onComprar} />)}
        </div>

        <div className="lp-f-garantia">
          {GARANTIAS.map(n => <span key={n}>{t(`landing.fund.garantia${n}`)}</span>)}
        </div>
        {estado.pagamentoDisponivel && (
          <p className="lp-f-subir">
            {t('landing.fund.subir')}{' '}
            <a href="#fundadores" onClick={e => { e.preventDefault(); onSubir(); }}>{t('landing.fund.subirLink')}</a>
          </p>
        )}

        <div className="lp-f-dois">
          <div className="lp-f-caixa">
            <h3>{t('landing.fund.comoTitulo')}</h3>
            <ol className="lp-f-passos">
              {PASSOS.map(n => <li key={n}><b>{t(`landing.fund.passo${n}Forte`)}</b> {t(`landing.fund.passo${n}`)}</li>)}
            </ol>
          </div>
          <div className="lp-f-caixa">
            <h3>{t('landing.fund.cartaTitulo')}</h3>
            <Carta estado={estado} />
          </div>
        </div>

        <Tabela estado={estado} />

        <p className="lp-f-nota">
          <b>{t('landing.fund.notaForte')}</b> {antesNota}
          <a href="#pre-registro" onClick={e => { e.preventDefault(); irAoFormulario(); }}>{t('landing.fund.notaLink')}</a>
          {depoisNota}
        </p>
      </div>
    </section>
  );
}
